// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * CollapsibleSection component for expandable table headers.
 * Provides expand/collapse functionality for section groupings.
 */
// No React import needed for TypeScript
import { ChevronRight, ExpandMore } from '@/constants/hardfin-standard-icons';
import { Box, Typography } from '@mui/material';

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * CollapsibleSection component
 */
export function CollapsibleSection(props: CollapsibleSectionProps) {
  const { title, isExpanded, onToggle } = props;

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: 2,
        cursor: 'pointer',
        userSelect: 'none',
        width: '100%',
        height: '100%',
        '&:hover': {
          opacity: 0.8,
        },
      }}
    >
      <Box
        sx={{
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {isExpanded ? (
          <ExpandMore fontSize="small" />
        ) : (
          <ChevronRight fontSize="small" />
        )}
      </Box>
      <Typography
        variant="subtitle2"
        sx={{
          pointerEvents: 'none',
          ml: 0.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}
